import React, { FC, useContext } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import { produce } from 'immer';

import { CheckboxWithBoldLabel } from 'src/presentation/components/forms/Checkbox';
import { Content, Footer, Row, ScrollRow } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import {
  NodeProps,
  DESCRIPTION_LABEL_TEXT,
  SIDE_PANEL_ROW_TYPES,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import NodeSelector from 'src/presentation/components/pages/ivr/components/NodeSelector';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const VoiceAutomationNode: FC<NodeProps> = ({
  attr,
  disabled,
  nodeList,
  onSelectNode,
  selectedOptionIndex,
  readOnly,
}) => {
  const { workingState, updateState, onBlur } = useContext(FormContext);

  const isFeatureEnabled = useIsFeatureEnabled();
  const isChannelManagementEnabled = isFeatureEnabled('enableChannelManagement');

  const currentNodeId = get(workingState, `${attr}.id`, '');

  const recordingEnabled = get(workingState, `${attr}.record`, false);
  const onRecordingChange = ({ checked }) => {
    const updatedState = produce(workingState, draft => {
      set(draft, `${attr}.record`, checked ? true : false);
    });
    updateState(updatedState);
    onBlur && onBlur({ currentState: updatedState });
  };

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid="name"
            disabled={disabled}
            label="Name"
            description="A unique name to identify and link this node with other nodes or Sidekick Profiles in Sidekick Entry Points"
          />
        </Row>
        {!isChannelManagementEnabled && (
          <Row>
            <InputField
              attr={`${attr}.contextData.routingRuleKeyword`}
              data-aid="contextData.routingRuleKeyword"
              disabled={disabled}
              label="Keyword"
              placeholder="wismo"
              description="Routing rule keyword"
            />
          </Row>
        )}
        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid="description"
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder="Describe"
            description="Describe the purpose of this node"
          />
        </Row>
        <Row>
          <CheckboxWithBoldLabel
            checked={!!recordingEnabled}
            attr={`${attr}.record`}
            data-aid="record"
            disabled={disabled}
            label="Enable voice automation recording"
            onChange={() => onRecordingChange({ checked: !recordingEnabled })}
            value={recordingEnabled ? 'checked' : ''}
            description="The recording of the Sidekick portion of the call is created and shown in the Conversation Timeline."
          />
        </Row>
      </Content>

      <Footer>
        <Row scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.handoffNodeId`}
            data-aid={`${attr}.handoffNodeId`}
            description="Hand off to another node"
            label="Exit Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            disabled={disabled}
            readOnly={readOnly}
          />
        </Row>

        <Row scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.closedNodeId`}
            data-aid={`${attr}.closedNodeId`}
            description="Sidekick resolved an issue"
            label="Issue Resolved Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            disabled={disabled}
            readOnly={readOnly}
          />
        </Row>

        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.ERROR_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.errorNodeId`}
            data-aid={`${attr}.errorNodeId`}
            description="If an error occurs in the Sidekick Voice node, go to this node"
            label="Error Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            disabled={disabled}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

export default VoiceAutomationNode;
